import React, { useState } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import ReactTooltip from 'react-tooltip';

import {
  ANALYTIC_EVENTS,
  trackWebGalleryEvent,
} from '../../../../../../web-gallery/utilities/analytics';
import withFavoritePhotosMutations from '../../../../../../web-gallery/hoc/withFavoritePhotosMutations';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import Button from '../../../../../../../components/global/Button';
import FavoritesIcon from '../../../../../../../components/icons/FavoritesIcon';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_favorite: PropTypes.bool,
  }).isRequired,
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  favoritePhoto: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  favoritePhotosCreate: PropTypes.func.isRequired,
  favoritePhotosDelete: PropTypes.func.isRequired,
  children: PropTypes.func,
  className: PropTypes.string,
  label: PropTypes.bool,
  tooltip: PropTypes.bool,
  onToggleFavorite: PropTypes.bool,
};

const MatchedPhotoFavoriteButton = ({
  icon = <FavoritesIcon color="#0d84fe" size={20} />,
  label,
  photo,
  album,
  children,
  className,
  tooltip,
  favoritePhotosCreate,
  favoritePhotosDelete,
  onToggleFavorite,
}) => {
  const { id: photoId, is_favorite: isFavorite } = photo;
  const [loading, setLoading] = useState(false);
  const openAlert = useOpenAlert();

  const onFavoriteToggle = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      if (isFavorite) {
        await favoritePhotosDelete(photo.id);
        trackWebGalleryEvent(
          ANALYTIC_EVENTS.PHOTO_BROWSER_DID_UNFAVORITE_PHOTO,
          {
            photo_id: photoId,
          },
        );
      } else {
        await favoritePhotosCreate(photoId, album?.id);
        trackWebGalleryEvent(ANALYTIC_EVENTS.PHOTO_BROWSER_DID_FAVORITE_PHOTO, {
          photo_id: photoId,
        });
      }
      onToggleFavorite?.({ isCreate: !isFavorite });
    } catch (error) {
      openAlert({
        type: 'error',
        title: 'Something went wrong while trying to favorite photo',
        message: getErrorString(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const altText = isFavorite ? 'Remove from favorites' : 'Add to favorites';

  if (typeof children === 'function') {
    return children({ onFavoriteToggle, photo, isFavorite });
  }

  return (
    <Button
      btnStyle="reset"
      onClick={onFavoriteToggle}
      className={className}
      disabled={loading}
      title={altText}
      data-tip={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
      data-for={`favorites-tooltip-${photoId}`}
      data-place="bottom"
    >
      {icon}
      {label && (
        <span className="d-none d-md-inline-block">
          {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
        </span>
      )}
      {tooltip && (
        <ReactTooltip id={`favorites-tooltip-${photoId}`} className="tooltip" />
      )}
    </Button>
  );
};

MatchedPhotoFavoriteButton.propTypes = propTypes;

export { MatchedPhotoFavoriteButton as WebGalleryFavoritePhotoButtonComponent };

export default compose(withFavoritePhotosMutations)(MatchedPhotoFavoriteButton);
